import React from 'react';

import { type Money, formatMoney } from '@betterment/core';
import { Box, Link, Separator, Text, type TextProps } from '@betterment/samba-react';

import { type FinancialSecurity, type SecurityPosition } from './data';
import { detailRowCss } from './SecurityGroupPositionSheet';

import { formatNumberWithDecimals } from '../../utils/NumbersHelper';

import type { SellButtonComponent } from './SecurityGroupPositionSheet';

type SecurityPositionItemProps = {
  securityPosition: SecurityPosition;
  variant: 'summary' | 'breakdown';
  sellButton?: React.ReactElement<React.ComponentProps<SellButtonComponent>>;
};

export function SecurityPositionItem({ securityPosition, variant, sellButton }: SecurityPositionItemProps) {
  switch (variant) {
    case 'breakdown':
      return <BreakdownBySubAccounts securityPosition={securityPosition} sellButton={sellButton} />;
    case 'summary':
      return <Summarized securityPosition={securityPosition} sellButton={sellButton} />;
  }
}

function Summarized({ securityPosition, sellButton }: Omit<SecurityPositionItemProps, 'variant'>) {
  const { financialSecurity } = securityPosition;
  const expenseRatio = financialSecurity.expenseRatio ?? null;

  return (
    <SecurityPositionItem.Layout financialSecurity={financialSecurity} sellButton={sellButton}>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.50' }}>
        <SecurityPositionItem.Balance amount={securityPosition.amount} />
        <SecurityPositionItem.Shares shares={securityPosition.shares} />
        <SecurityPositionItem.ExpenseRatio expenseRatio={expenseRatio} label="Fund fee per year" />
      </Box>
    </SecurityPositionItem.Layout>
  );
}

function BreakdownBySubAccounts({ securityPosition, sellButton }: Omit<SecurityPositionItemProps, 'variant'>) {
  const { financialSecurity, securityPositionsBySubAccount } = securityPosition;
  const expenseRatio = financialSecurity.expenseRatio ?? null;

  return (
    <SecurityPositionItem.Layout financialSecurity={financialSecurity} sellButton={sellButton}>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.50' }}>
        <SecurityPositionItem.CurrentWeight currentWeight={securityPosition.currentWeight} />
        <SecurityPositionItem.ExpenseRatio expenseRatio={expenseRatio} label="Fund fee" suffix="/yr" />
      </Box>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.100' }}>
        {securityPositionsBySubAccount.map(subAccountPosition => (
          <Box
            key={subAccountPosition.subAccount.id}
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'space.50',
              py: 'space.100',
            }}
          >
            <Text variant="text50">{subAccountPosition.subAccount.name}</Text>
            <SecurityPositionItem.Balance amount={subAccountPosition.amount} color="text.weak" />
            <SecurityPositionItem.Shares shares={subAccountPosition.shares} color="text.weak" />
          </Box>
        ))}
      </Box>
    </SecurityPositionItem.Layout>
  );
}

const Layout: React.FC<{
  financialSecurity: FinancialSecurity;
  sellButton?: React.ReactElement<React.ComponentProps<SellButtonComponent>>;
  children: React.ReactNode;
}> = ({ financialSecurity, sellButton, children }) => (
  <>
    <Separator />
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.200' }}>
      <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {'name' in financialSecurity && <Text>{financialSecurity.name}</Text>}
          {'url' in financialSecurity ? (
            <Link
              href={financialSecurity.url}
              target="_blank"
              rel="noopener noreferrer"
              css={{ fontSize: 'fontSize.50' }}
            >
              {financialSecurity.symbol}
            </Link>
          ) : (
            <Text>{financialSecurity.symbol}</Text>
          )}
        </Box>
        {sellButton}
      </Box>
      {children}
    </Box>
  </>
);

const CurrentWeight: React.FC<{
  currentWeight: number;
  color?: TextProps['color'];
}> = ({ currentWeight, color }) => {
  return (
    <Box css={detailRowCss}>
      <Text variant="text50" color={color}>
        Current weight
      </Text>
      <Text variant="text50" color={color}>
        {formatNumberWithDecimals(currentWeight, 1)}%
      </Text>
    </Box>
  );
};

const Balance: React.FC<{
  amount: Money;
  color?: TextProps['color'];
}> = ({ amount, color }) => {
  return (
    <Box css={detailRowCss}>
      <Text variant="text50" color={color}>
        Balance
      </Text>
      <Text variant="text50" color={color}>
        {formatMoney(amount)}
      </Text>
    </Box>
  );
};

const Shares: React.FC<{
  shares: number;
  color?: TextProps['color'];
}> = ({ shares, color }) => {
  return (
    <Box css={detailRowCss}>
      <Text variant="text50" color={color}>
        Shares
      </Text>
      <Text variant="text50" color={color}>
        {formatNumberWithDecimals(shares)} shares
      </Text>
    </Box>
  );
};

const ExpenseRatio: React.FC<{
  expenseRatio: number | null;
  label: string;
  suffix?: string;
  color?: TextProps['color'];
}> = ({ expenseRatio, label, suffix = '', color }) => {
  if (expenseRatio === null) {
    return null;
  }

  return (
    <Box css={detailRowCss}>
      <Text variant="text50" color={color}>
        {label}
      </Text>
      <Text variant="text50" color={color}>
        {formatNumberWithDecimals(expenseRatio)}%{suffix}
      </Text>
    </Box>
  );
};

SecurityPositionItem.Balance = Balance;
SecurityPositionItem.CurrentWeight = CurrentWeight;
SecurityPositionItem.ExpenseRatio = ExpenseRatio;
SecurityPositionItem.Layout = Layout;
SecurityPositionItem.Shares = Shares;
